import React from 'react';
import { Box, BoxProps } from '@tanium/react-box';
import { SimpleTooltip } from '@tanium/react-tooltip';
import { TypographyStyleProps } from '@tanium/style-system';
import { ThemeFontWeights } from '@tanium/theme-data';
import { Trans } from './localization';
import { OfText, Text } from './themeDefinitions';

export interface ResultsCountsMetricProps {
  /**
   * A custom label to describe each row. Defaults to "Items". Pass an empty string to omit.
   */
  itemsLabel?: string;
  /**
   * The count of items to display; the `x` in "x of y".
   */
  count: number;
  /**
   * The total number of items available; the `y` in "x of y".
   */
  totalCount?: number;
  /**
   * Content to display in a tooltip when hovering over the count of items.
   */
  tooltipContent?: React.ReactNode;
  /**
   * Font weight to use for normal text (e.g., the items label). Defaults to
   * `ThemeFontWeights.Normal`.
   */
  fontWeight?: TypographyStyleProps['fontWeight'];
  /**
   * Font weight to use for the count. Defaults to `ThemeFontWeights.Bold`.
   */
  countFontWeight?: TypographyStyleProps['fontWeight'];
}

const CountBox = (props: Omit<BoxProps<'span'>, 'as' | 'color'>) => (
  <Box {...props} as="span" color={Text} />
);

const OfBox = (props: Omit<BoxProps<'span'>, 'as' | 'color'>) => (
  <Box {...props} as="span" color={OfText} />
);
const ItemsLabel = (props: Omit<BoxProps<'span'>, 'as' | 'color'>) => (
  <Box {...props} as="span" color={Text} />
);

export const ResultsCountsMetricBlueStar = ({
  itemsLabel,
  count,
  totalCount,
  tooltipContent,
  fontWeight = ThemeFontWeights.Normal,
  countFontWeight = ThemeFontWeights.Bold,
}: ResultsCountsMetricProps) => {
  const showTotalCount = typeof totalCount === 'number';
  const omitLabel = itemsLabel === '';
  const useDefaultLabel = itemsLabel === undefined;
  const useSuppliedLabel = !!itemsLabel;

  const renderContent = () => {
    if (showTotalCount) {
      if (omitLabel) {
        return (
          <Trans count={totalCount} value={count}>
            <CountBox fontWeight={countFontWeight}>{'{{ value, INTEGER }}'}</CountBox>{' '}
            <OfBox>of</OfBox>{' '}
            <CountBox fontWeight={countFontWeight}>{'{{ count, INTEGER }}'}</CountBox>
          </Trans>
        );
      }
      if (useDefaultLabel) {
        return (
          <Trans count={totalCount} value={count}>
            <CountBox fontWeight={countFontWeight}>{'{{ value, INTEGER }}'}</CountBox>{' '}
            <OfBox>of</OfBox>{' '}
            <CountBox fontWeight={countFontWeight}>{'{{ count, INTEGER }}'}</CountBox>{' '}
            <ItemsLabel fontWeight={fontWeight}>Item</ItemsLabel>
          </Trans>
        );
      }
      if (useSuppliedLabel) {
        return (
          <Trans count={totalCount} value={count} itemsLabel={itemsLabel}>
            <CountBox fontWeight={countFontWeight}>{'{{ value, INTEGER }}'}</CountBox>{' '}
            <OfBox>of</OfBox>{' '}
            <CountBox fontWeight={countFontWeight}>{'{{ count, INTEGER }}'}</CountBox>{' '}
            <ItemsLabel fontWeight={fontWeight}>{{ itemsLabel }}</ItemsLabel>
          </Trans>
        );
      }
    } else {
      if (omitLabel) {
        return (
          <Trans count={count}>
            <CountBox fontWeight={countFontWeight}>{'{{ count, INTEGER }}'}</CountBox>
          </Trans>
        );
      }
      if (useDefaultLabel) {
        return (
          <Trans count={count}>
            <CountBox fontWeight={countFontWeight}>{'{{ count, INTEGER }}'}</CountBox>{' '}
            <ItemsLabel fontWeight={fontWeight}>Item</ItemsLabel>
          </Trans>
        );
      }
      if (useSuppliedLabel) {
        return (
          <Trans count={count}>
            <CountBox fontWeight={countFontWeight}>{'{{ count, INTEGER }}'}</CountBox>{' '}
            <ItemsLabel fontWeight={fontWeight}>{{ itemsLabel }}</ItemsLabel>
          </Trans>
        );
      }
    }
    return <></>;
  };

  return (
    <div>
      <SimpleTooltip content={tooltipContent}>
        {({ ref }) => <span ref={ref}>{renderContent()}</span>}
      </SimpleTooltip>
    </div>
  );
};
